import { Box, Button } from '@mui/material'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import api from '../api'
import {
  answersBeingSaved, flushPendingResults, pendingResultsCount, subscribeAnswersBeingSaved, subscribePendingResults
} from '../offlineMode/pendingResults'

// Save status for visitors of a shared link (no account): did the last answers reach the server?
// "Alles gespeichert", "Wird gespeichert …" or "Wartet auf Verbindung: 2 Antworten noch nicht gesendet".
export default function VisitorSaveStatus() {
  const { t } = useTranslation()
  const [pending, setPending] = useState(pendingResultsCount())
  const [saving, setSaving] = useState(answersBeingSaved())
  const [online, setOnline] = useState(navigator.onLine)
  const [touched, setTouched] = useState(false)
  const [sending, setSending] = useState(false)

  // retries run automatically; this is for "I have signal again, send now"
  const sendNow = async () => {
    setSending(true)
    try {
      await flushPendingResults((entry) => api.results.sendPending(entry), null)
    } finally {
      setSending(false)
    }
  }

  useEffect(() => subscribePendingResults(setPending), [])
  useEffect(() => subscribeAnswersBeingSaved((n) => {
    setSaving(n)
    setTouched(true)
  }), [])
  useEffect(() => {
    const on = () => setOnline(true)
    const off = () => setOnline(false)
    window.addEventListener('online', on)
    window.addEventListener('offline', off)
    return () => {
      window.removeEventListener('online', on)
      window.removeEventListener('offline', off)
    }
  }, [])

  // nothing answered yet: no status
  if (!touched && pending === 0 && online) return null

  let label = t('pendingResults.statusSaved')
  let tone = { bg: '#E7F4EC', fg: '#1E6B43', dot: '#1E7A4C' }
  if (pending > 0 || !online) {
    label = pending > 0 ? t('pendingResults.statusWaiting', { count: pending }) : t('pendingResults.statusOffline')
    tone = { bg: '#FFF4E0', fg: '#7A4A00', dot: '#B26A00' }
  } else if (saving > 0) {
    label = t('pendingResults.statusSaving')
    tone = { bg: '#EEF2F7', fg: '#3D4A5C', dot: '#7D8AA0' }
  }

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="visitor-save-status"
      sx={{
        display: 'flex', justifyContent: 'center', px: 2, py: 0.75, bgcolor: tone.bg, color: tone.fg,
        fontSize: 13, fontWeight: 600, gap: 1, alignItems: 'center'
      }}
    >
      <Box component="span" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: tone.dot, flex: 'none' }} />
      {label}
      {pending > 0 && (
        <Button
          size="small"
          onClick={sendNow}
          disabled={sending}
          sx={{ ml: 1, textTransform: 'none', fontWeight: 700, color: tone.fg, minWidth: 0, py: 0 }}
        >
          {sending ? t('pendingResults.sending') : t('pendingResults.sendNow')}
        </Button>
      )}
    </Box>
  )
}
